import { useState } from "react";
import { ArrowLeft, TrendingUp, Shield, Coins, Zap, BarChart3 } from "lucide-react";
import { Button } from "@/components/ui/button";

const PERSONAS = [
  {
    id: "growth",
    name: "Growth Investor",
    icon: TrendingUp,
    description: "Seek high-growth companies with strong upside potential.",
    strengths: "Strong in tech booms and expansion",
    weaknesses: "Vulnerable during recessions",
    color: "text-emerald-400",
    border: "border-emerald-400/30 hover:border-emerald-400/60",
    bg: "hover:bg-emerald-400/10",
  },
  {
    id: "value",
    name: "Value Investor",
    icon: Shield,
    description: "Find undervalued, defensive stocks with steady fundamentals.",
    strengths: "Resilient during downturns",
    weaknesses: "Slower gains in speculative booms",
    color: "text-blue-400",
    border: "border-blue-400/30 hover:border-blue-400/60",
    bg: "hover:bg-blue-400/10",
  },
  {
    id: "dividend",
    name: "Dividend Investor",
    icon: Coins,
    description: "Build income through dividend-paying stocks.",
    strengths: "Stable income and defense",
    weaknesses: "Slower capital growth",
    color: "text-amber-400",
    border: "border-amber-400/30 hover:border-amber-400/60",
    bg: "hover:bg-amber-400/10",
  },
  {
    id: "momentum",
    name: "Momentum Trader",
    icon: Zap,
    description: "Ride trends and capitalize on market momentum.",
    strengths: "Benefits from hype and trends",
    weaknesses: "Vulnerable to reversals",
    color: "text-purple-400",
    border: "border-purple-400/30 hover:border-purple-400/60",
    bg: "hover:bg-purple-400/10",
  },
  {
    id: "balanced",
    name: "Balanced Manager",
    icon: BarChart3,
    description: "Diversify across sectors for steady, risk-adjusted returns.",
    strengths: "Resilient and consistent",
    weaknesses: "Rarely the top performer",
    color: "text-cyan-400",
    border: "border-cyan-400/30 hover:border-cyan-400/60",
    bg: "hover:bg-cyan-400/10",
  },
];

const PERSONALITIES = [
  { id: "global_expansion", name: "Global Expansion Era", desc: "Broad-based economic growth" },
  { id: "tech_boom", name: "Tech Boom Era", desc: "Technology leads the market" },
  { id: "energy_crisis", name: "Energy Crisis Era", desc: "Energy volatility dominates" },
  { id: "high_inflation", name: "High Inflation Era", desc: "Rising costs pressure markets" },
  { id: "financial_crisis", name: "Financial Crisis Era", desc: "Banking stress and fear" },
  { id: "innovation_revolution", name: "Innovation Revolution", desc: "Breakthroughs drive growth" },
];

interface Props {
  onStart: (persona: string, personality: string) => void;
  loading: boolean;
  onBack: () => void;
}

const PersonaSelect = ({ onStart, loading, onBack }: Props) => {
  const [persona, setPersona] = useState<string | null>(null);
  const [personality, setPersonality] = useState("global_expansion");

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-card/60 backdrop-blur-md sticky top-0 z-50">
        <div className="container max-w-4xl mx-auto flex items-center gap-3 py-4 px-4">
          <Button variant="ghost" size="icon" onClick={onBack} className="text-muted-foreground hover:text-foreground hover:bg-muted/50">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <h1 className="font-heading font-bold text-lg text-foreground">Investing Fundamentals: Module</h1>
        </div>
      </header>

      <main className="container max-w-4xl mx-auto py-8 px-4 space-y-8">
        {/* Persona Selection */}
        <section>
          <h2 className="font-heading font-bold text-xl text-foreground mb-2">Choose Your Investor Persona</h2>
          <p className="text-muted-foreground text-sm mb-5">Your persona affects how you experience the market. Choose wisely.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {PERSONAS.map((p) => {
              const Icon = p.icon;
              const selected = persona === p.id;
              return (
                <button
                  key={p.id}
                  onClick={() => setPersona(p.id)}
                  className={`text-left p-4 rounded-xl border-2 transition-all duration-200 ${
                    selected
                      ? `${p.border.split(" ")[0]} bg-muted/30 shadow-lg`
                      : `border-border ${p.bg}`
                  }`}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <Icon className={`w-5 h-5 ${p.color}`} />
                    <span className={`font-heading font-bold text-sm ${selected ? p.color : "text-foreground/90"}`}>
                      {p.name}
                    </span>
                  </div>
                  <p className="text-muted-foreground text-xs mb-2">{p.description}</p>
                  <div className="text-xs space-y-0.5">
                    <p className="text-emerald-400/70">+ {p.strengths}</p>
                    <p className="text-red-400/70">- {p.weaknesses}</p>
                  </div>
                </button>
              );
            })}
          </div>
        </section>

        {/* Market Personality */}
        <section>
          <h2 className="font-heading font-bold text-xl text-foreground mb-2">Market Era</h2>
          <p className="text-muted-foreground text-sm mb-5">The macro environment shapes how sectors and stocks behave.</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {PERSONALITIES.map((mp) => (
              <button
                key={mp.id}
                onClick={() => setPersonality(mp.id)}
                className={`p-3 rounded-xl border-2 text-left transition-all ${
                  personality === mp.id
                    ? "border-accent/60 bg-accent/10"
                    : "border-border hover:border-border/80 hover:bg-muted/30"
                }`}
              >
                <span className={`font-heading font-semibold text-sm ${personality === mp.id ? "text-accent" : "text-foreground/80"}`}>
                  {mp.name}
                </span>
                <p className="text-muted-foreground text-xs mt-1">{mp.desc}</p>
              </button>
            ))}
          </div>
        </section>

        {/* Start Button */}
        <div className="text-center pt-4">
          <Button
            onClick={() => persona && onStart(persona, personality)}
            disabled={!persona || loading}
            className="px-8 py-3 bg-accent/20 border border-accent/50 text-accent hover:bg-accent/30 font-heading font-bold text-base shadow-[0_0_25px_hsl(var(--accent)/0.3)] disabled:opacity-40"
          >
            {loading ? "Starting..." : "Launch Simulation →"}
          </Button>
          <p className="text-muted-foreground/50 text-xs mt-3">Starting capital: $10,000 · 20 rounds</p>
        </div>
      </main>
    </div>
  );
};

export default PersonaSelect;
